import React from "react";
import { useGetResponsesApi } from "../../api/methods/use-get-responses-api";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useGetResponsesApiFilters } from "./use-get-responses-api-filters";
import { useRequiredParams } from "../../utils/use-required-params";
import { PageLoader } from "../../components/page-loader";
import { useSkipResponseApi } from "../../api/methods/use-skip-response-api";
import { ApiResponse, ApiResponseMention } from "../../api/types/api-response";
import { useUpdateResponseApi } from "../../api/methods/use-update-response-api";
import { useSupervisorMode } from "../../components/supervisor-mode-provider";
import { useCountReviewedResponses } from "../../utils/use-count-reviewed-responses";
import { TaggingModern as Tagging } from "./tagging-modern";
import { useGetPageUrl } from "../../utils/use-get-page-url";
import { getPreviousItemInArray } from "../../utils/array-utils";
import { trackTaggedResponse } from "../../utils/analytics";
import { useUnlockResponseStatisticsApi } from "../../api/methods/use-unlock-response-statistics-api";
import { useAuth } from "../../components/auth-routes-provider";
import { env } from "../../utils/env";

export const TaggingPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isSupervisorMode = useSupervisorMode();
  const auth = useAuth();
  const { organizationId, responseId } = useRequiredParams<{
    organizationId: string;
    responseId: string;
  }>();
  const themeStructureId = searchParams.get("themeStructureId") as string;
  const filters = useGetResponsesApiFilters();
  const isFirstLoadRef = React.useRef(true);
  const skipResponse = useSkipResponseApi();
  const updateResponse = useUpdateResponseApi();
  const { increment } = useCountReviewedResponses();
  const { getResponseUrl } = useGetPageUrl();
  const unlockResponseStatistics = useUnlockResponseStatisticsApi();

  // Track processed response IDs to prevent them from reappearing in buffer
  // Use sessionStorage to persist across page refreshes within the same session
  const getStorageKey = () =>
    `processedResponseIds_${organizationId}_${themeStructureId}_${filters.guesses}_${filters.skipped}_${filters.processed}_${filters.retags}`;

  const [processedResponseIds, setProcessedResponseIds] = React.useState<
    string[]
  >(() => {
    try {
      const stored = sessionStorage.getItem(getStorageKey());
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  React.useEffect(() => {
    try {
      sessionStorage.setItem(
        getStorageKey(),
        JSON.stringify(processedResponseIds),
      );
    } catch (error) {
      console.error("Failed to persist processedResponseIds:", error);
    }
  }, [processedResponseIds]);

  // Track whether the clearing effect has run at least once (skip on initial mount
  // so that processedResponseIds loaded from sessionStorage are not immediately wiped).
  const filterChangeRef = React.useRef(false);

  const [leftAdjustment, setLeftAdjustment] = React.useState(0);

  const {
    data: responsesResponse,
    isFetching: isResponsesFetching,
    isLoading: isResponsesLoading,
    refetch,
  } = useGetResponsesApi({
    organizationId: organizationId,
    themeStructureId: themeStructureId,
    ...filters,
    isFirstLoad: isFirstLoadRef.current,
  });
  isFirstLoadRef.current = false;

  const [prevResponsesResponse, setPrevResponsesResponse] =
    React.useState(responsesResponse);
  if (responsesResponse !== prevResponsesResponse) {
    setPrevResponsesResponse(responsesResponse);
    setLeftAdjustment(0);
  }

  const isInitialLoad =
    isResponsesLoading && responsesResponse.responses.length === 0;
  const processedResponseIdsSet = React.useMemo(
    () => new Set(processedResponseIds),
    [processedResponseIds],
  );
  const visibleResponses = React.useMemo(
    () =>
      responsesResponse.responses.filter(
        (response) => !processedResponseIdsSet.has(response.id),
      ),
    [responsesResponse.responses, processedResponseIdsSet],
  );

  React.useEffect(() => {
    // Skip the first mount: we want to preserve IDs loaded from sessionStorage.
    // Only clear when the user actually switches org/theme/filters mid-session.
    if (!filterChangeRef.current) {
      filterChangeRef.current = true;
      return;
    }
    setProcessedResponseIds([]);
    setLeftAdjustment(0);
    try {
      sessionStorage.removeItem(getStorageKey());
    } catch (error) {
      console.error("Failed to remove processedResponseIds:", error);
    }
  }, [
    organizationId,
    themeStructureId,
    filters.guesses,
    filters.skipped,
    filters.processed,
    filters.retags,
  ]);

  const currentResponse: ApiResponse | undefined = React.useMemo(
    () =>
      visibleResponses.find((response) => response.id === responseId) ||
      visibleResponses[0],
    [visibleResponses, responseId],
  );
  const lastKnownResponseRef = React.useRef<ApiResponse | null>(null);
  React.useEffect(() => {
    if (currentResponse) {
      lastKnownResponseRef.current = currentResponse;
    }
  }, [currentResponse]);
  const displayedResponse = currentResponse ?? lastKnownResponseRef.current;

  React.useEffect(() => {
    if (!responseId && !isInitialLoad && visibleResponses.length > 0) {
      navigate(
        getResponseUrl({
          organizationId: organizationId,
          responseId: visibleResponses[0].id,
          shouldPersistSearch: true,
        }),
      );
    }
  }, [
    responseId,
    isInitialLoad,
    visibleResponses,
    organizationId,
    navigate,
    getResponseUrl,
  ]);
  React.useEffect(() => {
    if (
      responseId &&
      visibleResponses.length > 0 &&
      !visibleResponses.some((response) => response.id === responseId)
    ) {
      navigate(
        getResponseUrl({
          organizationId: organizationId,
          responseId: visibleResponses[0].id,
          shouldPersistSearch: true,
        }),
      );
    }
  }, [responseId, visibleResponses, organizationId, navigate, getResponseUrl]);

  const shouldRefetchEmptyBatch =
    responsesResponse.responses.length === 0 &&
    responsesResponse.total > responsesResponse.responses.length;
  const shouldRefetchVisible =
    responsesResponse.responses.length > 0 && visibleResponses.length === 0;
  React.useEffect(() => {
    if (shouldRefetchEmptyBatch && !isResponsesFetching) {
      refetch();
    }
  }, [shouldRefetchEmptyBatch, isResponsesFetching, refetch]);
  React.useEffect(() => {
    if (shouldRefetchVisible && !isResponsesFetching) {
      refetch();
    }
  }, [shouldRefetchVisible, isResponsesFetching, refetch]);

  React.useEffect(() => {
    const handleBeforeUnload = () => {
      // Use fetch with keepalive flag to ensure unlock completes even when page is closing
      // This is more reliable than regular async calls which browsers often cancel during unload
      const baseURL = import.meta.env.DEV ? "/api" : env.BACKEND_URL;

      if (auth?.accessToken) {
        fetch(`${baseURL}/classifier/response_statistics/unlock`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${auth.accessToken}`,
          },
          body: JSON.stringify({}),
          keepalive: true, // Ensures request completes even after page unloads
        }).catch(() => {
          // Ignore errors during page unload
        });
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      unlockResponseStatistics().catch(() => {});
    };
  }, [unlockResponseStatistics, auth]);

  // Define handlers before any early returns (Rules of Hooks)
  const handleNextResponse = React.useCallback(async () => {
    if (!displayedResponse) {
      return;
    }

    const currentResponseId = displayedResponse.id;
    const remainingResponses = visibleResponses.filter(
      (r) => r.id !== currentResponseId,
    );

    try {
      const skippedResponse = await skipResponse.mutateAsync({
        organizationId: organizationId,
        responseId: currentResponseId,
        themeStructureId: themeStructureId,
      });

      if (!filters.skipped) {
        setLeftAdjustment((prev) => prev + 1);
      }

      // Persist synchronously before navigation: if the component is about to
      // unmount (navigate to /done), the useEffect cleanup would otherwise fire
      // before the sessionStorage write completes, losing the last ID.
      const newSkipIds = [...processedResponseIds, skippedResponse.id];
      try {
        sessionStorage.setItem(getStorageKey(), JSON.stringify(newSkipIds));
      } catch {
        // Ignore storage errors
      }
      setProcessedResponseIds(newSkipIds);

      if (remainingResponses.length > 0) {
        navigate(
          getResponseUrl({
            organizationId: organizationId,
            responseId: remainingResponses[0].id,
            shouldPersistSearch: true,
          }),
        );
      }
      // else: buffer exhausted — let shouldRefetchVisible trigger a refetch,
      // and shouldRedirectToDone navigate to /done only if nothing more remains.
    } catch {
      // Errors can be ignored
    }
  }, [
    displayedResponse,
    skipResponse,
    visibleResponses,
    processedResponseIds,
    organizationId,
    themeStructureId,
    navigate,
    getResponseUrl,
    filters.skipped,
  ]);

  const handlePreviousResponse = React.useCallback(() => {
    if (!displayedResponse) {
      return;
    }
    const previousResponse = getPreviousItemInArray(
      visibleResponses,
      displayedResponse,
    );

    navigate(
      getResponseUrl({
        organizationId: organizationId,
        responseId: previousResponse.id,
        shouldPersistSearch: true,
      }),
    );
  }, [
    displayedResponse,
    visibleResponses,
    navigate,
    getResponseUrl,
    organizationId,
  ]);

  const handleResponseUpdate = React.useCallback(
    (mentions: ApiResponseMention[]) => {
      if (!displayedResponse) {
        return;
      }

      const currentResponseId = displayedResponse.id;
      const remainingResponses = visibleResponses.filter(
        (r) => r.id !== currentResponseId,
      );

      updateResponse.mutate(
        {
          response: {
            ...displayedResponse,
            attributes: {
              ...displayedResponse.attributes,
              mentions: mentions,
            },
          },
          organizationId,
          isSupervisorMode,
          themeStructureId,
        },
        {
          onSuccess: (savedResponse) => {
            increment();
            setLeftAdjustment((prev) => prev + 1);
            trackTaggedResponse(
              displayedResponse.attributes.organization,
              currentResponseId,
            );

            // Persist synchronously before navigation: if the component is
            // about to unmount (navigate to /done), the useEffect cleanup
            // would otherwise fire before the sessionStorage write completes,
            // losing the last ID.
            const newSaveIds = [...processedResponseIds, savedResponse.id];
            try {
              sessionStorage.setItem(getStorageKey(), JSON.stringify(newSaveIds));
            } catch {
              // Ignore storage errors
            }
            setProcessedResponseIds(newSaveIds);

            if (remainingResponses.length > 0) {
              navigate(
                getResponseUrl({
                  organizationId: organizationId,
                  responseId: remainingResponses[0].id,
                  shouldPersistSearch: true,
                }),
              );
            }
            // else: buffer exhausted — let shouldRefetchVisible trigger a refetch,
            // and shouldRedirectToDone navigate to /done only if nothing more remains.
          },
        },
      );
    },
    [
      displayedResponse,
      updateResponse,
      organizationId,
      isSupervisorMode,
      themeStructureId,
      increment,
      visibleResponses,
      processedResponseIds,
      navigate,
      getResponseUrl,
    ],
  );

  const shouldRedirectToDone =
    !isResponsesFetching &&
    visibleResponses.length === 0 &&
    !shouldRefetchEmptyBatch &&
    !shouldRefetchVisible;
  React.useEffect(() => {
    if (shouldRedirectToDone) {
      navigate("/done");
    }
  }, [shouldRedirectToDone, navigate]);

  if (
    isInitialLoad ||
    shouldRefetchVisible ||
    (!displayedResponse && isResponsesFetching)
  ) {
    return <PageLoader />;
  }

  if (shouldRedirectToDone && !displayedResponse) {
    return <PageLoader />;
  }

  if (!displayedResponse) {
    return <PageLoader />;
  }

  return (
    <Tagging
      currentResponse={displayedResponse}
      responsesResponse={{
        ...responsesResponse,
        total: Math.max(0, responsesResponse.total - leftAdjustment),
      }}
      bufferCount={visibleResponses.length}
      onSkipResponse={handleNextResponse}
      onPreviousResponse={handlePreviousResponse}
      onUpdateResponse={handleResponseUpdate}
      isDisabled={skipResponse.isPending || updateResponse.isPending}
    />
  );
};
